<script setup>
  import { ref, watchEffect } from 'vue';
  import { useApiForecastModels } from '@/composables/useApiForecastModels';
  import { sortForecastModels } from '@/utils/sortForecastModels';
  import ToggleInputDefault from "@/components/controls/ToggleInputDefault.vue";
  import Icon from "@/components/icons/Icon.vue";

  const emit = defineEmits(['input:change']);

  const props = defineProps({
    id: {
      type: String,
      required: true
    },
    multiSelect: {
      type: Boolean,
      default: false
    },
    bounds: {
      type: Object,
      required: false
    },
    lat: {
      type: Number,
      required: false
    },
    lon: {
      type: Number,
      required: false
    }
  });  

  const apiForecastModels = useApiForecastModels();

  const FREE_PREVIEW_MODELS = [279];

  const selectedModel = defineModel();
  const modelData = ref(null);
  const loading = ref(true);

  async function getData() {
    if (!props.bounds && !props.lat && !props.lon) {
      loading.value = false;
      console.warn('No bounds or lat/lon provided to ModelSelector');
      return
    };

    loading.value = true;

    let response = null;

    if (props.lat && props.lon) {
      response = await apiForecastModels.byLatLon(props.lat, props.lon);
    } else if (props.bounds) {
      response = await apiForecastModels.byArea(props.bounds);
    } 

    const { data, error } = response;

    if (error.value) {
      console.error('Error fetching forecast models', error);
      loading.value = false;
      return
    }

    modelData.value = sortForecastModels(data.value.model_summary);
    loading.value = false;
  }

  function getAttributes(model) {
    return { id: model.model_id, value: model.model_id, inputType: 'radio', disabled: model.upgrade_available, }
  }

  function hasFreePreview(modelId) {
    return FREE_PREVIEW_MODELS.includes(modelId)
  }

  function handleInputChange(e) {
    emit('input:change', selectedModel.value);
  }

  watchEffect(() => getData())
</script>

<template>
  <div class="jwx-model-selector">
    <ul class="jwx-model-selector__model-list">
      <li v-for="model in modelData" :key="model.model_id" class="jwx-model-selector__model-item">
        <div class="jwx-model-selector__model">
          <ToggleInputDefault
            @input:change="handleInputChange"
            :attributes="getAttributes(model)"
            name="life-wind-model-selector"
            :disabled="model.upgrade_available && !hasFreePreview(model.model_id)"
            v-model="selectedModel"
          />
          <label
            :for="model.model_id"
            class="jwx-model-selector__label"
            :data-selected="selectedModel === model.model_id"
            :data-premium="model.is_premium"
          >
            <p class="jwx-model-selector__label-text" >{{ model.model_short_name }}</p>
            <span 
              v-if="hasFreePreview(model.model_id) && model.upgrade_available"
              class="jwx-model-selector__badge"
              data-type="preview"
            >
              <Icon name="sparkle" />
              Preview
            </span>
            <Icon v-else-if="model.upgrade_available" name="lock" />
            <Icon v-else-if="!model.upgrade_available && model.is_premium" name="premium-badge" />
          </label>
        </div>
      </li>

    </ul>
  </div>
</template>